/**
 * Construye resumen / reporte desde APIs CRUD ya desplegadas (si admin/resumen aún no existe).
 */
import axios from 'axios';
import { getDjangoAdminToken } from '@/services/djangoAdminAuth';
import { listarRegistros } from '@/services/DjangoSyncService';

const BACK = (process.env.VUE_APP_BACK_URL || '').replace(/\/$/, '');
const ACT = `${BACK}/actividades-estudyoo`;
const SIMCE = `${BACK}/simce`;
const SYNC = `${BACK}/estudyoo-sync`;
const JUE = `${BACK}/juegos-estudyoo`;

const NOTA_CATALOGO =
  'Capítulos, ejes y materias globales son compartidos. Cursos y alumnos son por colegio. '
  + '(Vista calculada en el navegador — despliega el backend para el endpoint admin/reporte-sync.)';

function hdrs() {
  const h = { Accept: 'application/json' };
  const t = getDjangoAdminToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

async function getList(url, params) {
  const { data } = await axios.get(url, { headers: hdrs(), params });
  return Array.isArray(data) ? data : data.results || [];
}

export async function getColegiosAdmin() {
  const urls = [
    `${ACT}/admin/colegios/`,
    `${SYNC}/admin/colegios/`,
    `${SIMCE}/colegios/`,
  ];
  for (const url of urls) {
    try {
      const list = await getList(url);
      if (list.length) return list;
    } catch (e) {
      if (e.response?.status !== 404) throw e;
    }
  }
  try {
    const { items } = (await listarRegistros()).data || {};
    const map = {};
    for (const r of items || []) {
      if (r.entidad === 'colegio' && r.django_id) {
        map[r.django_id] = {
          id: r.django_id,
          nombre: `Colegio sync #${r.id_externo || '?'}`,
          rbd: '',
          activo: true,
        };
      }
    }
    const inferidos = Object.values(map);
    if (inferidos.length) return inferidos;
  } catch {
    /* ignore */
  }
  return [];
}

function capsPorEje(capitulos, ejeId) {
  return capitulos.filter((c) => c.eje_id === ejeId).length;
}

function ejesPorMateria(ejes, materiaId) {
  return ejes.filter((e) => e.materia_id === materiaId);
}

function buildEspejoFromItems(items) {
  const byExt = {};
  const djangoToExt = {};
  for (const r of items) {
    if (r.entidad === 'colegio') {
      djangoToExt[r.django_id] = r.id_externo;
    }
    const ext = r.colegio_id_externo;
    if (!ext) continue;
    if (!byExt[ext]) byExt[ext] = {};
    byExt[ext][r.entidad] = (byExt[ext][r.entidad] || 0) + 1;
  }
  return { byExt, djangoToExt };
}

function espejoRow(esp) {
  const total = Object.values(esp).reduce((a, b) => a + b, 0);
  return {
    cursos: esp.curso || 0,
    estudiantes: esp.estudiante || 0,
    contratos: esp.contrato || 0,
    detalles_contrato: esp.detalle_contrato || 0,
    asignaturas: esp.asignatura || 0,
    total_registros: total,
  };
}

async function loadBaseLists() {
  const [materias, ejes, capitulos, actividades, asignaciones, cursos, estudiantes, preguntasJuego, registros] =
    await Promise.all([
      getList(`${ACT}/materias/`),
      getList(`${ACT}/ejes-tematicos/`),
      getList(`${ACT}/capitulos/`),
      getList(`${ACT}/actividades/`),
      getList(`${ACT}/asignaciones-curso/`),
      getList(`${SIMCE}/cursos/`),
      getList(`${SIMCE}/estudiantes/`),
      getList(`${JUE}/preguntas/`).catch(() => []),
      listarRegistros().then((r) => r.data).catch(() => ({ items: [] })),
    ]);
  const { byExt, djangoToExt } = buildEspejoFromItems(registros.items || []);
  return {
    materias,
    ejes,
    capitulos,
    actividades,
    asignaciones,
    cursos,
    estudiantes,
    preguntasJuego,
    byExt,
    djangoToExt,
  };
}

function contarEjes(ejes, capitulos) {
  if (ejes.length) return ejes.length;
  const ids = new Set((capitulos || []).map((c) => c.eje_id).filter(Boolean));
  return ids.size;
}

function catalogoCompartido(base) {
  const { materias, ejes, capitulos, actividades, preguntasJuego } = base;
  const porCodigo = {};
  let sinColegio = 0;
  let conColegio = 0;
  for (const m of materias) {
    if (m.colegio_id) conColegio += 1;
    else sinColegio += 1;
    const key = m.codigo || m.id;
    if (!porCodigo[key]) porCodigo[key] = [];
    porCodigo[key].push(m);
  }
  const catalogoMaterias = Object.keys(porCodigo)
    .sort((a, b) => {
      const oa = Math.min(...porCodigo[a].map((x) => x.orden || 0));
      const ob = Math.min(...porCodigo[b].map((x) => x.orden || 0));
      return oa - ob;
    })
    .map((codigo) => {
      const grupo = porCodigo[codigo];
      const rep = grupo.reduce((best, m) => {
        const ej = ejesPorMateria(ejes, m.id);
        const caps = ej.reduce((n, e) => n + capsPorEje(capitulos, e.id), 0);
        const bestEj = ejesPorMateria(ejes, best.id);
        const bestCaps = bestEj.reduce((n, e) => n + capsPorEje(capitulos, e.id), 0);
        return caps > bestCaps ? m : best;
      }, grupo[0]);
      const ejRep = ejesPorMateria(ejes, rep.id);
      return {
        id: rep.id,
        nombre: rep.nombre,
        codigo: rep.codigo,
        ejes: ejRep.length,
        capitulos: ejRep.reduce((n, e) => n + capsPorEje(capitulos, e.id), 0),
        instancias_colegio: grupo.filter((x) => x.colegio_id).length,
        es_global: grupo.some((x) => !x.colegio_id),
      };
    });
  const totalEjes = contarEjes(ejes, capitulos);
  return {
    nota: NOTA_CATALOGO,
    materias_catalogo: catalogoMaterias.length,
    materias_globales: sinColegio,
    materias_totales: materias.length,
    materias_propias_colegio: conColegio,
    ejes: totalEjes,
    capitulos: capitulos.length,
    capitulos_sin_nivel: capitulos.filter((c) => !c.nivel_simce).length,
    actividades: actividades.length,
    actividades_activas: actividades.filter((a) => a.activa !== false).length,
    preguntas_actividad: 0,
    preguntas_juego: preguntasJuego.length,
    materias: catalogoMaterias,
  };
}

/** Deduplica filas de asignaturas por código (catálogo compartido). */
export function dedupeMateriasReporte(items) {
  const porCodigo = {};
  for (const m of items || []) {
    if (!m?.id && !m?.codigo) continue;
    const key = m.codigo || m.id;
    if (!porCodigo[key]) {
      porCodigo[key] = {
        id: m.id,
        nombre: m.nombre || '—',
        codigo: m.codigo || '',
        ejes: Number(m.ejes) || 0,
        capitulos: Number(m.capitulos) || 0,
        instancias_colegio: 0,
        es_global: m.colegio_id === null || m.colegio_id === undefined ? !!m.es_global : false,
      };
    }
    const row = porCodigo[key];
    row.instancias_colegio += 1;
    if (m.es_global || m.colegio_id === null) row.es_global = true;
    const caps = Number(m.capitulos) || 0;
    if (caps >= row.capitulos) {
      row.capitulos = caps;
      row.ejes = Number(m.ejes) || row.ejes;
      row.nombre = m.nombre || row.nombre;
      row.id = m.id || row.id;
    }
  }
  return Object.values(porCodigo).sort((a, b) =>
    (a.nombre || '').localeCompare(b.nombre || '', 'es'),
  );
}

export function materiasDesdeFilasColegio(colegios) {
  const flat = [];
  for (const col of colegios || []) {
    for (const m of col.materias || []) {
      flat.push({ ...m });
    }
  }
  return dedupeMateriasReporte(flat);
}

async function materiasDesdeListadoApi() {
  const [materias, ejes, capitulos] = await Promise.all([
    getList(`${ACT}/materias/`),
    getList(`${ACT}/ejes-tematicos/`),
    getList(`${ACT}/capitulos/`),
  ]);
  return dedupeMateriasReporte(
    materias.map((m) => {
      const ej = ejesPorMateria(ejes, m.id);
      return {
        ...m,
        ejes: ej.length,
        capitulos: ej.reduce((n, e) => n + capsPorEje(capitulos, e.id), 0),
      };
    }),
  );
}

/** Rellena catalogo.materias si el backend devolvió conteos pero tabla vacía. */
export async function enriquecerCatalogoReporte(catalogo = {}, colegios = []) {
  if (catalogo.materias?.length) {
    return {
      catalogo: {
        ...catalogo,
        materias_catalogo: catalogo.materias_catalogo ?? catalogo.materias.length,
      },
      enriquecido: false,
    };
  }
  let materias = materiasDesdeFilasColegio(colegios);
  if (!materias.length && (catalogo.materias_propias_colegio || catalogo.materias_totales)) {
    materias = await materiasDesdeListadoApi();
  }
  if (!materias.length) {
    return { catalogo, enriquecido: false };
  }
  return {
    catalogo: {
      ...catalogo,
      materias,
      materias_catalogo: materias.length,
    },
    enriquecido: true,
  };
}

/** Añade materias al detalle expandible de cada colegio si el backend no las incluyó. */
export async function enriquecerColegiosMaterias(colegios = []) {
  const faltan = (colegios || []).some(
    (c) => !c.materias?.length && (c.django?.materias_propias || 0) > 0,
  );
  if (!faltan) return colegios;
  const [materias, ejes, capitulos] = await Promise.all([
    getList(`${ACT}/materias/`),
    getList(`${ACT}/ejes-tematicos/`),
    getList(`${ACT}/capitulos/`),
  ]);
  return (colegios || []).map((col) => {
    if (col.materias?.length) return col;
    const props = materias
      .filter((m) => m.colegio_id === col.id)
      .map((m) => {
        const ej = ejesPorMateria(ejes, m.id);
        return {
          id: m.id,
          nombre: m.nombre,
          codigo: m.codigo,
          ejes: ej.length,
          capitulos: ej.reduce((n, e) => n + capsPorEje(capitulos, e.id), 0),
        };
      });
    if (!props.length) return col;
    return { ...col, materias: props };
  });
}

function filaColegio(col, base) {
  const {
    materias,
    ejes,
    capitulos,
    asignaciones,
    cursos,
    estudiantes,
    byExt,
    djangoToExt,
  } = base;
  const id = col.id;
  const cursosCol = cursos.filter((c) => c.colegio_id === id);
  const cursoIds = new Set(cursosCol.map((c) => c.id));
  const estCol = estudiantes.filter((e) => e.colegio_id === id);
  const asigCol = asignaciones.filter((a) => cursoIds.has(a.curso_id));
  const ext = djangoToExt[id] || null;
  const esp = byExt[ext] || {};
  const materiasPropias = materias.filter((m) => m.colegio_id === id);

  return {
    id,
    nombre: col.nombre,
    rbd: col.rbd || '',
    activo: col.activo !== false,
    id_externo: ext,
    sincronizado: !!ext,
    ultima_sesion: null,
    django: {
      cursos: cursosCol.length,
      estudiantes: estCol.length,
      estudiantes_activos: estCol.filter((e) => e.activo !== false).length,
      contratos: 0,
      materias_propias: materiasPropias.length,
      asignaciones: asigCol.length,
      asignaciones_activas: asigCol.filter((a) => a.activa !== false).length,
    },
    espejo_sync: espejoRow(esp),
    materias: materiasPropias.map((m) => {
      const ej = ejesPorMateria(ejes, m.id);
      return {
        id: m.id,
        nombre: m.nombre,
        codigo: m.codigo,
        ejes: ej.length,
        capitulos: ej.reduce((n, e) => n + capsPorEje(capitulos, e.id), 0),
      };
    }),
  };
}

export async function fetchReporteSyncFallback() {
  const [colegios, base] = await Promise.all([
    getColegiosAdmin(),
    loadBaseLists(),
  ]);
  const filas = colegios.map((col) => filaColegio(col, base));
  const catalogo = catalogoCompartido(base);
  return {
    catalogo_compartido: catalogo,
    colegios: filas,
    totales: {
      colegios: filas.length,
      colegios_activos: filas.filter((f) => f.activo).length,
      colegios_sincronizados: filas.filter((f) => f.sincronizado).length,
      cursos: filas.reduce((n, f) => n + f.django.cursos, 0),
      estudiantes_activos: filas.reduce((n, f) => n + f.django.estudiantes_activos, 0),
    },
    _fallback: true,
  };
}

export async function fetchResumenFallback(params = {}) {
  const colegioId = params.colegio_id;
  const base = await loadBaseLists();
  const catalogo = catalogoCompartido(base);

  if (!colegioId) {
    return {
      colegio: null,
      alcance: 'global',
      conteos: {
        cursos: base.cursos.length,
        estudiantes: base.estudiantes.filter((e) => e.activo !== false).length,
        estudiantes_total: base.estudiantes.length,
        materias: catalogo.materias_totales,
        ejes: catalogo.ejes,
        capitulos: catalogo.capitulos,
        capitulos_sin_nivel: catalogo.capitulos_sin_nivel,
        actividades: catalogo.actividades,
        actividades_activas: catalogo.actividades_activas,
        preguntas_actividad: 0,
        materiales_actividad: 0,
        materiales_capitulo: 0,
        asignaciones: base.asignaciones.length,
        asignaciones_activas: base.asignaciones.filter((a) => a.activa !== false).length,
        preguntas_juego: catalogo.preguntas_juego,
      },
      sync: { colegio_id_externo: null, registros_espejo: 0, ultima_sesion: null },
      _fallback: true,
    };
  }

  const colegios = await getColegiosAdmin();
  const col = colegios.find((c) => c.id === colegioId);
  if (!col) throw new Error('Colegio no encontrado');
  const fila = filaColegio(col, base);
  const ext = fila.id_externo;

  return {
    colegio: { id: col.id, nombre: col.nombre, rbd: col.rbd || '' },
    alcance: 'colegio',
    idCurso: null,
    conteos: {
      cursos: fila.django.cursos,
      estudiantes: fila.django.estudiantes_activos,
      estudiantes_total: fila.django.estudiantes,
      materias: base.materias.filter((m) => !m.colegio_id || m.colegio_id === colegioId).length,
      ejes: contarEjes(base.ejes, base.capitulos),
      capitulos: catalogo.capitulos,
      capitulos_sin_nivel: catalogo.capitulos_sin_nivel,
      actividades: catalogo.actividades,
      actividades_activas: catalogo.actividades_activas,
      preguntas_actividad: 0,
      materiales_actividad: 0,
      materiales_capitulo: 0,
      asignaciones: fila.django.asignaciones,
      asignaciones_activas: fila.django.asignaciones_activas,
      preguntas_juego: catalogo.preguntas_juego,
    },
    sync: {
      colegio_id_externo: ext,
      registros_espejo: fila.espejo_sync.total_registros,
      ultima_sesion: null,
    },
    _fallback: true,
  };
}

/** Intenta varias URLs admin; si 404 o 5xx, usa fallback local cuando exista. */
export async function getAdminJson(paths, params, fallbackFn) {
  let lastErr;
  for (const url of paths) {
    try {
      return await axios.get(url, { headers: hdrs(), params }).then((r) => r.data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404 || (fallbackFn && status >= 500)) {
        lastErr = e;
        continue;
      }
      throw e;
    }
  }
  if (fallbackFn) return fallbackFn(params);
  throw lastErr || new Error('Endpoint no disponible');
}

export const ADMIN_COLEGIOS_URLS = [
  `${ACT}/admin/colegios/`,
  `${SYNC}/admin/colegios/`,
];

export const ADMIN_RESUMEN_URLS = [
  `${ACT}/admin/resumen/`,
  `${SYNC}/admin/resumen/`,
];

export const ADMIN_REPORTE_URLS = [
  `${ACT}/admin/reporte-sync/`,
  `${SYNC}/admin/reporte-sync/`,
];
